import { debounce } from '@mui/material';
import { canadaProvinces, usStates } from '@provision/location-utils';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { identity, isNullish } from 'remeda';
import { FormAutocompleteProps, FormField } from 'src/components';
import { SEARCH_DEBOUNCE_DELAY } from 'src/constants/delays';
import { useBrandOptions, usePartnersOptions } from 'src/graphql';
import { mapPlatformUsersToSortedOptions } from 'src/graphql/entities/platform-user/platform-user.mapping';
import { useSearch } from 'src/graphql/entities/search/use-search';
import { EntityNames } from 'src/graphql/types';
import { BrandRaw } from 'src/types';
import {
  GoogleMapsAddressComponentTypes,
  promisifyGetDetails,
  useGoogleMapsSuggestions,
  useScrollPaginateHandler,
} from 'src/utils/hooks';

export enum AddSiteFormFields {
  Address1 = 'address1',
  Address2 = 'address2',
  City = 'city',
  State = 'state',
  Zip = 'zip',
  BusinessName = 'businessName',
  ClientSegment = 'clientSegment',
  Clients = 'clients',
  AccountManager = 'accountManager',
  OrgNotes = 'orgNotes',
  Partner = 'partner',
}

type ClientOption = {
  id: string;
  label: string;
};

export interface NewAddressFormValues {
  [AddSiteFormFields.AccountManager]: { id: string; label: string; value: string };
  [AddSiteFormFields.ClientSegment]: { id: string; label: string; data: BrandRaw | null };
  [AddSiteFormFields.State]: { value: string; label: string };
  [AddSiteFormFields.Address1]: string;
  [AddSiteFormFields.Address2]: string;
  [AddSiteFormFields.BusinessName]: string;
  [AddSiteFormFields.City]: string;
  [AddSiteFormFields.Zip]: string;
  [AddSiteFormFields.OrgNotes]: string;
  [AddSiteFormFields.Clients]?: ClientOption[];
  [AddSiteFormFields.Partner]?: {
    id: number;
    value: number;
  };
}

const commonProps = {
  textFieldProps: {
    variant: 'outlined',
    size: 'small',
  },
};

export const statesOptions = [...usStates, ...canadaProvinces].map((state) => ({
  value: state.value,
  label: `${state.text} (${state.value})`,
}));

export const getClientOptionLabel = (client: { fullName: string; primaryEmail?: string }) =>
  `${client.fullName}${client.primaryEmail ? ` (${client.primaryEmail})` : ''}`;

export function useNewSiteForm({
  partnerId,
  defaultValues,
  isUpdateForm,
  onAddNewClient,
  setFormValue,
}: {
  partnerId?: number | null;
  defaultValues?: NewAddressFormValues;
  isUpdateForm?: boolean;
  onAddNewClient?: () => void;
  setFormValue?: ReturnType<typeof useForm>['setValue'];
}) {
  const partnerOptions = usePartnersOptions();

  // TODO: Investigate the need of local state
  const [address1Input, setAddress1Input] = useState('');
  const [cityInput, setCityInput] = useState('');
  const [postalCodeInput, setPostalCodeInput] = useState('');
  const [stateInput, setStateInput] = useState<null | {
    value: string;
    label: string;
  }>(null);
  const [partner, setPartner] = useState<null | {
    id: number;
    value: number;
    label?: string;
    supportEmail?: string;
  }>(defaultValues?.partner ?? null);

  const optionBrands = useBrandOptions(partner?.id ?? partnerId, {
    desc: true,
  });

  const [searchUsersTerm, setSearchUsersTerm] = useState('');
  const [searchClientsTerm, setSearchClientsTerm] = useState('');

  const {
    records: activeUserRecords,
    isLoading: usersLoading,
    loadMore: usersLoadMore,
    hasMore: usersHasMore,
  } = useSearch({
    entityName: EntityNames.PlatformUser,
    searchTerm: searchUsersTerm,
    filter: [
      {
        attribute: 'isActive',
        operator: '=',
        value: 'true',
      },
    ],
    partnerId: partner?.id ?? partnerId,
    shouldIncludePlatformPartnerId: false,
    showResultsWhenEmptySearchTerm: true,
  }) ?? {};

  const userOptions = useMemo(
    () => mapPlatformUsersToSortedOptions(activeUserRecords),
    [activeUserRecords],
  );

  const clientsFilter = useMemo(() => {
    return [
      // This filter should be present, since it is also used in multi-tenancy mode
      // when partner-id header is not included in the request.
      {
        attribute: 'partnerId',
        operator: '=',
        value: String(partner?.id ?? partnerId),
      },
    ];
  }, [partner?.id, partnerId]);

  const {
    records: clientsToAdd = [],
    loadMore: clientsLoadMore,
    hasMore: clientsHasMore,
    isLoading: clientsIsLoading,
  } = useSearch({
    entityName: EntityNames.Client,
    searchTerm: searchClientsTerm,
    filter: clientsFilter,
    showResultsWhenEmptySearchTerm: true,
    skip: isNullish(partner?.id ?? partnerId),
  }) ?? {};

  const clientOptions = useMemo(
    () => [
      ...clientsToAdd.map((client) => ({
        id: client.clientId,
        label: getClientOptionLabel(client),
      })),
    ],
    [clientsToAdd],
  );

  const usersHandleScroll = useScrollPaginateHandler({
    loadMore: usersLoadMore,
    enabled: usersHasMore,
    thresholdPx: 100,
  });

  const clientsHandleScroll = useScrollPaginateHandler({
    loadMore: clientsLoadMore,
    enabled: clientsHasMore,
    thresholdPx: 100,
  });

  const { suggestions } = useGoogleMapsSuggestions({
    input: address1Input,
  });

  const suggestionOptions = useMemo(
    () =>
      suggestions.map((suggestion) => ({
        ...suggestion,
        label: suggestion.description,
        id: suggestion.place_id,
      })),
    [suggestions],
  );

  useEffect(() => {
    setPartner(defaultValues?.partner ?? null);
  }, [defaultValues?.partner]);

  const handleAddressInputChange = debounce((e: React.SyntheticEvent<HTMLInputElement>) => {
    const target = e?.target as HTMLInputElement;
    if (target) {
      setAddress1Input(target.value);
    }
  }, SEARCH_DEBOUNCE_DELAY);

  const handleAddressChange = useCallback(
    async (
      _: React.SyntheticEvent<HTMLInputElement>,
      value?:
        | string
        | ({
            label: string;
            id: string;
          } & google.maps.places.AutocompletePrediction),
    ) => {
      if (!value) {
        return;
      }

      if (typeof value === 'string') {
        setAddress1Input(value);
        setFormValue?.(AddSiteFormFields.Address1, value);
        return;
      }

      const streetAddress = value.structured_formatting?.main_text ?? value.label;
      setAddress1Input(streetAddress);
      setFormValue?.(AddSiteFormFields.Address1, streetAddress);

      const details = await promisifyGetDetails(value.id);
      details?.address_components?.forEach((addressComponent) => {
        if (addressComponent.types.includes(GoogleMapsAddressComponentTypes.PostalCode)) {
          setPostalCodeInput(addressComponent.long_name);
        }

        if (
          addressComponent.types.includes(
            GoogleMapsAddressComponentTypes.AdministrativeAreaLevel1,
          )
        ) {
          const currentState = statesOptions.find((state) =>
            state.label.startsWith(addressComponent.long_name),
          ) || { label: '', value: '' };
          setStateInput(currentState);
        }

        if (addressComponent.types.includes(GoogleMapsAddressComponentTypes.Locality)) {
          setCityInput(addressComponent.long_name);
        }
      });
    },
    [setFormValue],
  );

  const newSiteFormFields = useMemo(() => {
    const fields = [
      {
        ...commonProps,
        type: 'autocomplete',
        name: AddSiteFormFields.Address1,
        label: 'Address 1',
        required: 'Address 1 is required',
        blurOnSelect: false,
        defaultValue: address1Input,
        value: address1Input,
        textFieldProps: {
          ...commonProps.textFieldProps,
          placeholder: 'Start typing address',
        },
        autocompleteProps: {
          freeSolo: true,
          onInputChange: handleAddressInputChange,
          filterOptions: (options) => options,
          onChange: handleAddressChange,
        },
        options: suggestionOptions,
      },
      {
        ...commonProps,
        type: 'textfield',
        name: AddSiteFormFields.Address2,
        label: 'Address 2',
      },
      {
        ...commonProps,
        type: 'textfield',
        name: AddSiteFormFields.City,
        label: 'City',
        required: 'City is required',
        // TODO: Currently, defaultValue is needed to fix an issue with the placeholder
        // we need to check a proper solution to that issue
        value: cityInput,
        defaultValue: cityInput,
      },
      {
        ...commonProps,
        type: 'autocomplete',
        name: AddSiteFormFields.State,
        label: 'State/Province',
        options: statesOptions,
        blurOnSelect: false,
        // TODO: Currently, defaultValue is needed to fix an issue with the placeholder
        // we need to check a proper solution to that issue
        value: stateInput,
        defaultValue: stateInput,
      },
      {
        ...commonProps,
        type: 'textfield',
        name: AddSiteFormFields.Zip,
        label: 'Postal Code',
        // TODO: Currently, defaultValue is needed to fix an issue with the placeholder
        // we need to check a proper solution to that issue
        value: postalCodeInput,
        defaultValue: postalCodeInput,
      },
      {
        ...commonProps,
        type: 'textfield',
        name: AddSiteFormFields.BusinessName,
        label: 'Business Name',
      },
      ...(isNullish(partner?.id ?? partnerId)
        ? []
        : [
            {
              ...commonProps,
              type: 'autocomplete',
              name: AddSiteFormFields.ClientSegment,
              label: 'Client Segment',
              required: 'Client Segment is required',
              options: optionBrands,
              blurOnSelect: false,
            },
          ]),
      ...(isUpdateForm || isNullish(partner?.id ?? partnerId)
        ? []
        : [
            {
              ...commonProps,
              type: 'autocomplete',
              name: AddSiteFormFields.Clients,
              label: 'Clients',
              options: clientOptions,
              creatable: true,
              defaultValue: undefined,
              onAddNewItem: onAddNewClient,
              onAddNewItemLabel: '+ Create New Client',
              blurOnSelect: false,
              autocompleteProps: {
                loading: clientsIsLoading,
                multiple: true,
                ListboxProps: {
                  onScroll: clientsHandleScroll,
                  sx: {
                    maxHeight: '18rem',
                  },
                },
                filterOptions: identity,
                onClose: () => {
                  setSearchClientsTerm('');
                },
                onInputChange: (_: unknown, value: string) => {
                  setSearchClientsTerm(value);
                },
              },
            },
          ]),
      {
        ...commonProps,
        type: 'autocomplete',
        name: AddSiteFormFields.AccountManager,
        label: 'Account Manager',
        options: userOptions,
        blurOnSelect: false,
        autocompleteProps: {
          loading: usersLoading,
          ListboxProps: {
            onScroll: usersHandleScroll,
            sx: {
              maxHeight: '18rem',
            },
          },
          filterOptions: identity,
          onClose: () => {
            setSearchUsersTerm('');
          },
          onInputChange: (_, value: string) => {
            setSearchUsersTerm(value);
          },
        },
      },
      {
        type: 'rich-text-bubble',
        name: AddSiteFormFields.OrgNotes,
        label: 'Site Notes',
      },
    ] as FormField[];

    if (isNullish(partnerId)) {
      fields.unshift({
        type: 'autocomplete',
        name: AddSiteFormFields.Partner,
        label: 'Partner',
        required: 'Partner is required',
        options: partnerOptions,
        blurOnSelect: false,
        textFieldProps: {
          variant: 'outlined',
          size: 'small',
          placeholder: 'Start typing partner',
          autoFocus: true,
        },
        // TODO: Currently, defaultValue is needed to fix an issue with the placeholder
        // we need to check a proper solution to that issue
        value: partner,
        defaultValue: partner,
        autocompleteProps: {
          onChange: (
            _,
            newValue: {
              id: number;
              value: number;
            },
          ) => {
            setPartner(newValue);
            setFormValue?.(AddSiteFormFields.ClientSegment, null);
            setFormValue?.(AddSiteFormFields.Clients, []);
          },
        },
      } as FormAutocompleteProps);
    }

    return fields;
  }, [
    address1Input,
    handleAddressInputChange,
    handleAddressChange,
    suggestionOptions,
    cityInput,
    stateInput,
    postalCodeInput,
    partner,
    partnerId,
    optionBrands,
    isUpdateForm,
    clientOptions,
    onAddNewClient,
    clientsIsLoading,
    clientsHandleScroll,
    userOptions,
    usersLoading,
    usersHandleScroll,
    partnerOptions,
    setFormValue,
  ]);

  const clearFields = useCallback(() => {
    setAddress1Input('');
    setCityInput('');
    setStateInput(null);
    setPartner(null);
    setPostalCodeInput('');
  }, []);

  return { newSiteFormFields, clearFields };
}
